import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { useI18n } from '../i18n';
import { useAuth } from '../auth/AuthContext';
import { useAppNavigation } from '../navigation/AppNavigationContext';
import { isVoiceAssistantEnabled } from './isVoiceAssistantEnabled';
import { useAssistantController } from './useAssistantController';
import { AssistantPanel } from './AssistantPanel';
import { PLACY_ICON_SRC } from '../components/OffizzBrandMark';
import { unlockAssistantSpeech } from './assistantSpeech';
import styles from './AssistantHost.module.css';

type AssistantContextValue = {
  rememberAssetForBooking: (id: string, record: Record<string, unknown>) => void;
  openAssistant: () => void;
};

const AssistantContext = createContext<AssistantContextValue | null>(null);

export function useAssistant(): AssistantContextValue | null {
  return useContext(AssistantContext);
}

export function AssistantProvider({ children }: { children: ReactNode }) {
  const enabled = isVoiceAssistantEnabled();
  const assistant = useAssistantController();
  const [open, setOpen] = useState(false);
  const { t } = useI18n();
  const { isLoggedIn } = useAuth();
  const { view } = useAppNavigation();
  const showFab = view !== 'auth' && view !== 'connectionTest';

  const openAssistant = useCallback(() => {
    unlockAssistantSpeech();
    setOpen(true);
  }, []);
  const onClose = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (isLoggedIn) {
      return;
    }
    setOpen(false);
    assistant.clearMessages();
  }, [isLoggedIn, assistant.clearMessages]);

  const value = useMemo<AssistantContextValue>(
    () => ({
      rememberAssetForBooking: assistant.rememberAssetForBooking,
      openAssistant,
    }),
    [assistant.rememberAssetForBooking, openAssistant],
  );

  if (!enabled) {
    return <>{children}</>;
  }

  return (
    <AssistantContext.Provider value={value}>
      {children}
      {typeof document !== 'undefined'
        ? createPortal(
            <>
              {showFab ? (
                <button
                  type="button"
                  className={styles.fab}
                  onClick={openAssistant}
                  aria-label={t('assistant.fab.label')}
                  title={t('assistant.fab.label')}
                >
                  <img
                    src={PLACY_ICON_SRC}
                    alt=""
                    className={styles.fabLogo}
                    draggable={false}
                  />
                </button>
              ) : null}
              <AssistantPanel
                open={open}
                onClose={onClose}
                messages={assistant.messages}
                thinking={assistant.thinking}
                pendingBook={assistant.pendingBook}
                onSubmitCommand={assistant.runTextCommand}
                onNewSearch={assistant.resetSearch}
                onClear={assistant.clearMessages}
                onConfirmBook={assistant.confirmPendingBook}
                onCancelBook={assistant.cancelPendingBook}
              />
            </>,
            document.body,
          )
        : null}
    </AssistantContext.Provider>
  );
}
